import { fetchLatest } from '@digiwedge/tour-registry-client';
import { evaluateGuards, stickyRng, type Traits } from '@digiwedge/tour-targeting';
import {
  LocalStoragePolicyStore,
  shouldShow,
  markRun,
  checkGlobalCap,
  setActive,
  clearActive,
} from '@digiwedge/tour-policy';
import { tour as coreTour } from '@digiwedge/tour-core';
import type { Tour as TourDsl } from '@digiwedge/tour-dsl';
import type { BaseTourStep } from '@digiwedge/tour-core/src/lib/types.js';
import { makeCmpConsentProvider } from '@digiwedge/tour-core';

type Ctx = {
  baseUrl: string;
  tenantId: string;
  env: 'dev' | 'stage' | 'prod';
  tourId: string;
  authToken?: string;
  traits: Traits;
  storagePrefix: string;
  previewToken?: string;
};

/**
 * Maps DSL tour into runtime start, using selector-only steps.
 * The overlay supports optional selector on steps; we pass it through via step.selector
 * but need a concrete Element for step.target. Resolve late in overlay if detached.
 */
function toRuntimeSteps(
  dsl: TourDsl,
): Array<BaseTourStep<{ title: string; body: string }> & { selector?: string }> {
  return dsl.steps.map((s) => {
    const fallback = document.body as Element;
    const target = s.selector
      ? (document.querySelector(
          `[data-onboard-id="${CSS.escape(s.selector)}"]`,
        ) as Element | null) || fallback
      : fallback;
    return {
      id: s.id,
      content: { title: s.title, body: s.body },
      target,
      selector: s.selector,
      spotlightPadding: 8,
    };
  });
}

export async function maybeRunTour(ctx: Ctx): Promise<void> {
  const { baseUrl, tenantId, env, tourId, authToken, traits, storagePrefix, previewToken } = ctx;
  const consent = makeCmpConsentProvider();
  if (!consent.analyticsAllowed()) return;

  const etagKey = `${storagePrefix}tour:etag:${tourId}`;
  const cachedTag = previewToken ? undefined : (localStorage.getItem(etagKey) ?? undefined);
  const res = await fetchLatest({
    baseUrl,
    tenantId,
    env,
    tourId,
    etag: cachedTag,
    token: authToken,
    previewToken,
  });
  if (res.status === 304) return;
  if (res.status !== 200) return;
  const dslTour = res.tour;
  const etag = res.etag;
  if (!previewToken && etag) localStorage.setItem(etagKey, etag);

  const isPreview = !!previewToken;
  let store: LocalStoragePolicyStore | undefined;
  if (!isPreview) {
    const rng = stickyRng(`${tenantId}|${tourId}|${traits.userId ?? 'anon'}`);
    const guardsOk = evaluateGuards(dslTour, traits, rng);
    if (!guardsOk) {
      try {
        console.info('tour_bootstrap', {
          tourId,
          preview: false,
          consent: consent.analyticsAllowed(),
          targeted: false,
          policy: 'n/a',
        });
      } catch {
        /* noop */
      }
      return;
    }
    store = new LocalStoragePolicyStore(storagePrefix);
    // Global cap
    const allowConcurrent = (() => {
      const v = (dslTour as Record<string, unknown>)['allowConcurrent'];
      return typeof v === 'boolean' ? v : false;
    })();
    const cap = checkGlobalCap({ tourId, allowConcurrent, store });
    if (!cap.ok) {
      try {
        console.info('tour_bootstrap', {
          tourId,
          preview: false,
          consent: consent.analyticsAllowed(),
          targeted: true,
          policy: cap.reason ?? 'blocked',
        });
      } catch {
        /* noop */
      }
      return;
    }
    const policy = shouldShow({
      tourId,
      tourVersion: dslTour.version,
      cooldownDays: dslTour.cooldownDays,
      store,
    });
    if (!policy.ok) {
      try {
        console.info('tour_bootstrap', {
          tourId,
          preview: false,
          consent: consent.analyticsAllowed(),
          targeted: true,
          policy: policy.reason ?? 'blocked',
        });
      } catch {
        /* noop */
      }
      return;
    }
  }

  const steps = toRuntimeSteps(dslTour);
  // Start the core tour using adapter UI already present in app
  try {
    console.info('tour_bootstrap', {
      tourId,
      preview: isPreview,
      consent: consent.analyticsAllowed(),
      targeted: true,
      policy: isPreview ? 'preview' : 'ok',
    });
  } catch {
    /* noop */
  }
  coreTour.start(dslTour.id, steps, { storageKey: `${storagePrefix}${dslTour.id}` });
  if (!isPreview && store) setActive(store, tourId);
  if (isPreview) {
    try {
      const ttlMs = 30 * 60 * 1000; // 30 minutes
      const payload = JSON.stringify({
        origin: window.location.origin,
        expiresAt: Date.now() + ttlMs,
      });
      sessionStorage.setItem('tour.preview', payload);
      const u = new URL(window.location.href);
      u.searchParams.delete('tourPreviewToken');
      window.history.replaceState({}, '', u.toString());
    } catch {
      /* noop */
    }
  }
  // Best-effort: mark completion when finish event fires
  if (!isPreview && store) {
    const off = coreTour.on((e) => {
      if (e.type === 'finish') {
        markRun(store, tourId, dslTour.version);
        clearActive(store, tourId);
        off();
      }
    });
  }
}
